import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatDateBR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const LOGO_URL =
    "https://customer-assets-eiarnc6j.emergentagent.net/job_carpool-manager-6/artifacts/qyxqg7dd_logo%20Padr%C3%A3o.png";

const VEHICLE_LABELS = {
    onibus: "Ônibus", caminhonete: "Caminhonete", carro: "Carro", van: "Van", outro: "Outro",
};

const STATUS_LABELS = {
    pendente: "PENDENTE", aprovada: "APROVADA", negada: "NEGADA", concluida: "CONCLUÍDA",
};

const VINCULO_LABELS = {
    cooperado: "Cooperado", nao_cooperado: "Não cooperado",
    setor_social: "Setor Social (COOMFLONA)", entidade: "Entidade externa",
    comunidade: "Comunidade", outro: "Outro",
};

export default function RequestPrint() {
    const { id } = useParams();
    const { user } = useAuth();
    const [req, setReq] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        api.get(`/requests/${id}`).then((res) => {
            setReq(res.data);
            setLoading(false);
        }).catch(() => { setError(true); setLoading(false); });
    }, [id]);

    const doPrint = () => window.print();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
        );
    }

    if (error || !req) {
        return (
            <div className="min-h-screen flex items-center justify-center flex-col gap-4">
                <div className="text-muted-foreground">Solicitação não encontrada</div>
                <Button onClick={() => window.close()}>Fechar</Button>
            </div>
        );
    }

    const today = new Date().toLocaleDateString("pt-BR");

    return (
        <div className="print-root min-h-screen bg-gray-100 p-8" data-testid="request-print-page">
            <div className="no-print max-w-3xl mx-auto mb-4 flex items-center justify-between">
                <Button variant="outline" onClick={() => window.close()} data-testid="btn-close-req-print">Fechar</Button>
                <Button onClick={doPrint} className="gap-2" data-testid="btn-print-req">
                    <Printer className="w-4 h-4" /> Imprimir
                </Button>
            </div>

            <div className="ficha max-w-3xl mx-auto bg-white shadow-md" data-testid="ficha-request-content">
                <div className="ficha-header flex items-center gap-4 p-6 border-b-2 border-black">
                    <div className="w-20 h-20 bg-emerald-900 rounded-lg flex items-center justify-center p-1 shrink-0 border-2 border-emerald-800 shadow-md">
                        <img src={LOGO_URL} alt="COOMFLONA" className="w-full h-full object-contain" />
                    </div>
                    <div className="flex-1 text-center">
                        <div className="font-extrabold text-lg leading-tight">COOPERATIVA MISTA DA FLONA DO TAPAJÓS – COOMFLONA</div>
                        <div className="text-sm mt-1">CNPJ: 07.315.165/0001-00 – Insc. Est: 15-249-128-7</div>
                        <div className="text-sm">coomflona@hotmail.com</div>
                    </div>
                </div>

                <div className="text-center py-4 border-b border-black relative">
                    <div className="font-extrabold text-lg">SOLICITAÇÃO DE TRANSPORTE</div>
                    <div className="text-sm mt-1">Setor de Transporte</div>
                    <div className="absolute right-6 top-1/2 -translate-y-1/2">
                        <div className="text-[10px] uppercase text-gray-500">Status</div>
                        <div className="font-bold text-sm">{STATUS_LABELS[req.status]}</div>
                    </div>
                </div>

                <div className="p-8 space-y-4 text-base">
                    <div className="grid grid-cols-2 gap-6">
                        <Field label="Nome do solicitante" value={req.solicitante_nome} />
                        <Field label="Vínculo" value={VINCULO_LABELS[req.vinculo] || "Outro"} />
                    </div>
                    <div className="grid grid-cols-2 gap-6">
                        <Field label="Entidade / comunidade" value={req.entidade || "—"} />
                        <Field label="Telefone" value={req.telefone || "—"} />
                    </div>
                    <div className="grid grid-cols-2 gap-6">
                        <Field label="Origem" value={req.origem || "—"} />
                        <Field label="Destino" value={req.destino} />
                    </div>
                    {req.datas && req.datas.length > 1 ? (
                        <div>
                            <div className="text-xs uppercase tracking-wider text-gray-600 font-semibold">Datas da viagem ({req.datas.length})</div>
                            <div className="mt-1 font-medium border-b border-dashed border-gray-400 pb-1">
                                {req.datas.map(formatDateBR).join("  •  ")}
                            </div>
                        </div>
                    ) : (
                        <Field label="Data da viagem" value={formatDateBR(req.data_viagem)} />
                    )}
                    <div className="grid grid-cols-3 gap-4">
                        <Field label="Nº de dias" value={String(req.dias || 1)} />
                        <Field label="Tipo de veículo" value={VEHICLE_LABELS[req.tipo_veiculo] || req.tipo_veiculo} />
                        <Field label="Nº passageiros" value={String(req.num_passageiros)} />
                    </div>
                    {(req.veiculo_placa || req.motorista_nome) && (
                        <div className="grid grid-cols-2 gap-6">
                            <Field label="Placa do veículo" value={req.veiculo_placa || "—"} />
                            <Field label="Motorista" value={req.motorista_nome || "—"} />
                        </div>
                    )}
                    <div className="grid grid-cols-2 gap-6">
                        {req.valor_estimado > 0 ? (
                            <Field label="Valor estimado" value={req.valor_estimado.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })} />
                        ) : <div />}
                        {req.chave_pix ? (
                            <Field label="Chave PIX" value={req.chave_pix} />
                        ) : <div />}
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-wider text-gray-600 font-semibold">Motivo da solicitação</div>
                        <div className="mt-1 font-medium border border-dashed border-gray-400 rounded p-2 min-h-[56px] whitespace-pre-wrap">{req.motivo}</div>
                    </div>
                    {req.observacoes && (
                        <div>
                            <div className="text-xs uppercase tracking-wider text-gray-600 font-semibold">Observações</div>
                            <div className="mt-1 text-sm whitespace-pre-wrap">{req.observacoes}</div>
                        </div>
                    )}
                    {req.status !== "pendente" && req.observacoes_decisao && (
                        <div className="mt-4 border-t border-black/20 pt-3">
                            <div className="text-xs uppercase tracking-wider text-gray-600 font-semibold">Decisão ({STATUS_LABELS[req.status]})</div>
                            <div className="mt-1 text-sm whitespace-pre-wrap italic">{req.observacoes_decisao}</div>
                            {req.decidido_por && <div className="text-xs text-gray-500 mt-1">Por {req.decidido_por} em {req.decidido_em ? new Date(req.decidido_em).toLocaleDateString("pt-BR") : "—"}</div>}
                        </div>
                    )}
                </div>

                <div className="px-8 pb-8 pt-12 grid grid-cols-3 gap-6">
                    <div className="text-center">
                        <div className="border-t border-black pt-2 text-sm font-medium">Solicitante</div>
                        <div className="text-xs text-gray-600 mt-1">{req.solicitante_nome}</div>
                    </div>
                    <div className="text-center">
                        <div className="border-t border-black pt-2 text-sm font-medium">Setor de Transporte</div>
                        <div className="text-xs text-gray-600 mt-1">{user?.name || "—"}</div>
                    </div>
                    <div className="text-center">
                        <div className="border-t border-black pt-2 text-sm font-medium">Coordenação / Aprovador</div>
                        <div className="text-xs text-gray-600 mt-1">{req.decidido_por || "—"}</div>
                    </div>
                </div>

                <div className="px-8 pb-4 flex justify-between text-[10px] text-gray-500 border-t border-gray-300 pt-3">
                    <div>Emitido em: {today} {user?.name ? `— por ${user.name}` : ""}</div>
                    <div>Solicitação nº: {req.id.slice(0, 8).toUpperCase()}</div>
                </div>
            </div>

            <style>{`
                @media print {
                    body { background: white !important; }
                    .no-print { display: none !important; }
                    .print-root { padding: 0 !important; background: white !important; }
                    .ficha { box-shadow: none !important; max-width: 100% !important; }
                    @page { size: A4; margin: 15mm; }
                }
            `}</style>
        </div>
    );
}

function Field({ label, value }) {
    return (
        <div>
            <div className="text-xs uppercase tracking-wider text-gray-600 font-semibold">{label}</div>
            <div className="mt-1 font-medium border-b border-dashed border-gray-400 pb-1 min-h-[24px]">{value || "—"}</div>
        </div>
    );
}
